import { Group, ScrollArea, Stack } from '@mantine/core';
import i18n from '../../../../../base-dictionary';
import { useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { setPerformanceActivePanel } from '../../../../../store/strategy-store/strategy-store.actions';
import { secondaryBase, skyDark } from '../../../../../styles/design-tokens';
import { Divider } from '../../../../../ui-library/divider/divider';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { getLatestDate } from '../../related-tasks-tab/related-tasks-tab.helpers';
import { useChartData } from '../cost-tab.hooks';
import { ChartDataType } from '../cost-tab.type';

interface SideSectionOtherProps<T> {
  useData: () => T[];
  dataKey: 'otherEpicsData' | 'otherTeamsData';
  showRelatedTasks: (params: { epic?: string; team?: string; date: Date }) => void;
  getDisplayName: (item: T) => string;
  getTitle?: (item: T) => string;
  getItemId: (item: T) => string;
  paramKey: 'epic' | 'team';
}

export function SideSectionOther<T>({
  useData,
  dataKey,
  showRelatedTasks,
  getDisplayName,
  getTitle,
  getItemId,
  paramKey,
}: SideSectionOtherProps<T>) {
  const data = useData();
  const chartData = useChartData();
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  const otherItem = chartData.find((item) => item.type === ChartDataType.Other);

  if (!otherItem || !otherItem[dataKey]) {
    return null;
  }

  const otherData = otherItem[dataKey]!;
  const otherIds = Object.keys(otherData);

  const otherItems = data
    .filter((item) => otherIds.includes(getItemId(item)))
    .sort((a, b) => (otherData[getItemId(b)]?.size ?? 0) - (otherData[getItemId(a)]?.size ?? 0));

  const latestDate = getLatestDate(initiative);

  const handleItemClick = (itemId: string) => {
    if (!latestDate) {
      return;
    }
    const params = { [paramKey]: itemId, date: latestDate };
    showRelatedTasks(params);
  };

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Text weight="bold" size="medium">
          {i18n.t('strategy.initiatives.other')}
        </Text>
        <Icon
          name="close"
          onClick={() => setPerformanceActivePanel(null)}
          size={20}
          color={skyDark}
          aria-label="Close panel"
        />
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0}>
        <Stack gap={24}>
          {otherItems.map((item) => {
            const itemId = getItemId(item);
            const itemData = otherData[itemId];

            return (
              <Stack gap={8} key={itemId}>
                <Group gap={4}>
                  <Text
                    size="small"
                    lineHeight="none"
                    color={secondaryBase}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleItemClick(itemId)}
                  >
                    {getDisplayName(item)}
                  </Text>
                  <Text size="small" lineHeight="none" color={skyDark}>
                    (${itemData?.size ?? 0})
                  </Text>
                  <Text size="small" lineHeight="none" color={skyDark}>
                    ({(itemData?.percentage ?? 0).toFixed(2)}%)
                  </Text>
                </Group>
                {getTitle && (
                  <Text
                    size="small"
                    lineHeight="none"
                    weight="medium"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleItemClick(itemId)}
                  >
                    {getTitle(item)}
                  </Text>
                )}
              </Stack>
            );
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
